import React from 'react';
import { ErrorState } from '../kit';

// The four states every block on the client page can be in, kept visibly distinct
// so a reader never has to guess which one they are looking at:
//
//   loading   → a skeleton shaped like the content       (each block)
//   empty     → neutral copy, and the next action if any (each block)
//   no access → a muted lock: "You don't have access to X" — NOT red, no retry,
//               because trying again cannot help
//   error     → red, with a retry
//
// The api layer rethrows only `{ message }` (the Postgres code is dropped), so a
// permission denial is recognised from its message.

const NO_ACCESS = /permission denied|\b42501\b|not authori[sz]ed|forbidden|insufficient privilege/i;

/**
 * A read that has neither data nor an error has not answered yet. useTileData starts
 * idle (loading false) and only flips to loading inside an effect, so its very first
 * render looks identical to "finished with nothing" — which would flash empty or
 * no-access copy before the read has even started.
 * @param {{data:*, error:*}} tile  A useTileData result.
 * @returns {boolean}
 */
export const isPending = (tile) => !tile.data && !tile.error;

/**
 * Whether a failed read was a permission denial rather than a fault.
 * @param {*} error  A string, an Error, or `{ message }`.
 * @returns {boolean}
 */
export const isNoAccess = (error) => {
  const text = typeof error === 'string' ? error : error?.message || '';
  return NO_ACCESS.test(text);
};

const LockIcon = () => (
  <svg className="h-3.5 w-3.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </svg>
);

/**
 * The muted no-access line.
 * @param {object} props
 * @param {string} props.what   What they cannot see: "this client's work".
 * @param {string} [props.className]
 */
export function NoAccess({ what, className = '' }) {
  return (
    <p role="note" className={`inline-flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 ${className}`}>
      <LockIcon /> You don't have access to {what}.
    </p>
  );
}

/**
 * A failed read, rendered as the right one of the two: no-access (muted, no retry)
 * for a permission denial, else the red error with a retry.
 *
 * @param {object} props
 * @param {*} props.error         What the read threw (or a truthy flag).
 * @param {string} props.what     "this client's work" — used by the no-access line.
 * @param {string} props.message  The red error copy: "Couldn't load this client's work."
 * @param {() => void} [props.onRetry]
 * @param {"inset"|"strip"} [props.variant]
 * @param {string} [props.padding]  Inset padding.
 * @param {string} [props.className]
 */
export function BlockError({ error, what, message, onRetry, variant = 'inset', padding = 'pt-3', className = '' }) {
  if (isNoAccess(error)) return <NoAccess what={what} className={`${variant === 'inset' ? padding : ''} ${className}`.trim()} />;
  return <ErrorState variant={variant} padding={padding} className={className} message={message} onRetry={onRetry} />;
}
